import { Badge } from "app/components/atoms/badge";
import { ServerSidePaginationButtons } from "app/components/atoms/button";
import { FormHeader } from "app/components/atoms/layout";
import { Select } from "app/components/atoms/select";
import Table from "app/components/atoms/table";
import Typography from "app/components/atoms/typography";
import { useToast } from "app/components/atoms/toast/toast";
import { DateRangeInputMolecule } from "app/components/molecules/input";
import api from "app/integration/api";
import { usePaginatedCollector } from "app/integration/common/hooks";
import MainLayout from "app/layouts/main";
import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { defaultReportDates } from "app/common/reportDateRange";
import { angka, tagihanEfektif, totalDiskon } from "app/common/reportNumber";

// Ukuran per-request saat mengumpulkan seluruh baris untuk export — bukan
// batas total. Export melakukan LOOP mengambil halaman demi halaman sampai
// `totalPages` tercapai, jadi jumlah baris yang diekspor selalu = jumlah baris
// yang cocok dengan filter, berapapun besarnya (ditemukan lewat re-review
// Devin AI: batas tunggal 10.000 baris sebelumnya diam-diam memotong laporan
// untuk filter luas).
const EXPORT_PAGE_SIZE = 1000;
// Jaring pengaman supaya loop tidak berjalan tanpa batas kalau backend
// mengirim `totalPages` yang salah — bukan batas baris normal. Kalau
// tersentuh, pengguna DIBERITAHU lewat toast (bukan diam-diam terpotong).
const EXPORT_MAX_PAGES = 500;

const breadcrumbItems = [
  { label: "Laporan Toko", href: "#" },
  { label: "Transaksi", href: "#" },
];

const statusBadgeColor = (status) => {
  switch (status) {
    case "paid":
      return "green";
    case "canceled":
      return "red";
    case "pending":
    case "hold":
      return "yellow";
    case "close":
      return "gray";
    default:
      return "gray";
  }
};

function StoreReportTransactionsPage() {
  const [warehouses, setWarehouses] = useState([]);
  const [params, setParams] = useState({
    warehouse_id: null,
    ...defaultReportDates(),
    page: 1,
    limit: 25,
  });

  useEffect(() => {
    const loadWarehouses = async () => {
      try {
        const response = await api.warehouse.index({ limit: 1000 });
        setWarehouses(response?.data ?? []);
      } catch (err) {
        setWarehouses([]);
      }
    };
    loadWarehouses();
  }, []);

  const warehouseOptions = [
    { value: null, label: "Semua Outlet" },
    ...warehouses.map((warehouse) => ({
      value: warehouse.id,
      label: warehouse.name,
    })),
  ];

  const [transactions, , loader, , paginationProps] = usePaginatedCollector({
    modelName: "storeReport",
    methodName: "transactions",
    params,
  });

  const [exporting, setExporting] = useState(false);
  const { showToast } = useToast();

  // Export mengambil SEMUA baris yang cocok dengan filter aktif lewat request
  // terpisah, BUKAN memakai `transactions` yang sudah dipotong paginasi 25
  // baris/halaman — kalau tidak, tombol "Export Excel" diam-diam cuma
  // mengekspor halaman yang sedang tampil, bukan seluruh laporan yang
  // difilter (ditemukan lewat review Devin AI pada PR #69).
  //
  // Mengambil per HALAMAN (loop sampai `totalPages` habis), bukan satu
  // request ber-limit tinggi — limit tunggal diam-diam memotong laporan
  // begitu filter menghasilkan lebih banyak baris dari limit itu (ditemukan
  // lewat re-review Devin AI pada commit 2860ab6).
  //
  // Error di tengah loop MEMBATALKAN seluruh export — tidak menghasilkan
  // file kosong yang terlihat seperti laporan valid (juga ditemukan lewat
  // re-review yang sama: sebelumnya `catch { rows = [] }` menelan error dan
  // tetap mengunduh workbook kosong).
  const exportExcel = async () => {
    setExporting(true);
    let rows = [];
    try {
      let page = 1;
      let totalPages = 1;
      do {
        const response = await api.storeReport.transactions({
          warehouse_id: params.warehouse_id,
          start_date: params.start_date,
          end_date: params.end_date,
          page,
          limit: EXPORT_PAGE_SIZE,
        });
        const pageRows = Array.isArray(response?.data) ? response.data : [];
        rows = rows.concat(pageRows);
        totalPages = response?.pagination?.totalPages || 1;
        if (pageRows.length === 0) break;
        page += 1;
      } while (page <= totalPages && page <= EXPORT_MAX_PAGES);

      if (page > EXPORT_MAX_PAGES && page <= totalPages) {
        showToast(
          `Laporan terlalu besar — hanya ${rows.length} baris pertama yang diekspor. Persempit rentang tanggal atau outlet untuk laporan lengkap.`,
          "warning"
        );
      }
    } catch (err) {
      setExporting(false);
      showToast(
        "Gagal mengambil data laporan untuk export. Coba lagi.",
        "error"
      );
      return;
    }
    setExporting(false);

    const dataForExcel = rows.map((row) => ({
      Kode: row.code,
      Outlet: row.warehouse_name,
      Tanggal: row.sold_at,
      Status: row.status,
      Kasir: row.cashier_name,
      "Metode Bayar": row.payment_method_name,
      Subtotal: angka(row.sub_total),
      Diskon: totalDiskon(row),
      "Grand Total": tagihanEfektif(row),
    }));
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(dataForExcel, {
      header: [
        "Kode",
        "Outlet",
        "Tanggal",
        "Status",
        "Kasir",
        "Metode Bayar",
        "Subtotal",
        "Diskon",
        "Grand Total",
      ],
    });
    worksheet["!cols"] = [
      { wch: 16 },
      { wch: 22 },
      { wch: 20 },
      { wch: 12 },
      { wch: 20 },
      { wch: 18 },
      { wch: 16 },
      { wch: 16 },
      { wch: 16 },
    ];
    XLSX.utils.book_append_sheet(workbook, worksheet, "Transaksi");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const formattedDate = new Date().toISOString().split("T")[0];
    link.download = `laporan_transaksi_${formattedDate}.xlsx`;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 100);
  };

  return (
    <MainLayout
      activeSidebarNavigation="store-report"
      activeSidebarChild="transactions"
      breadcrumbItems={breadcrumbItems}
      pageTitle="Laporan Transaksi"
      headingButtons={[
        {
          label: exporting ? "Mengekspor..." : "Export Excel",
          type: "button",
          onClick: exportExcel,
          disabled: exporting,
        },
      ]}
    >
      <Table.Container>
        <Table.SearchBarWrapper>
          <FormHeader>
            <div className="col-span-2">
              <DateRangeInputMolecule testId="store-report-transactions-date-range"
                label="Tanggal"
                from={params.start_date}
                to={params.end_date}
                onFromChange={(e) =>
                  setParams({ ...params, start_date: e.target.value, page: 1 })
                }
                onToChange={(e) =>
                  setParams({ ...params, end_date: e.target.value, page: 1 })
                }
              />
            </div>
            <Select testId="store-report-transactions-outlet-select"
              label="Outlet"
              placeholder="Filter Outlet"
              options={warehouseOptions}
              value={params.warehouse_id}
              onChange={(e) =>
                setParams({ ...params, warehouse_id: e.target.value, page: 1 })
              }
              absolute={false}
            />
          </FormHeader>
        </Table.SearchBarWrapper>
        <Table.Wrapper asIndex={false}>
          <Table.Header>
            <Table.HeaderRow>
              <Table.Heading>Kode</Table.Heading>
              <Table.Heading>Outlet</Table.Heading>
              <Table.Heading>Tanggal</Table.Heading>
              <Table.Heading>Status</Table.Heading>
              <Table.Heading>Kasir</Table.Heading>
              <Table.Heading>Metode Bayar</Table.Heading>
              <Table.Heading>
                <p className="text-right">Subtotal</p>
              </Table.Heading>
              <Table.Heading>
                <p className="text-right">Diskon</p>
              </Table.Heading>
              <Table.Heading>
                <p className="text-right">Grand Total</p>
              </Table.Heading>
            </Table.HeaderRow>
          </Table.Header>
          <Table.Body>
            {Array.isArray(transactions) &&
              transactions.map((row, index) => (
                <Table.BodyRow key={index}>
                  <Table.Cell>{row.code}</Table.Cell>
                  <Table.Cell>{row.warehouse_name}</Table.Cell>
                  <Table.Cell gradient={500} fontWeight="light">
                    <Typography.Date
                      size="sm"
                      format="dd MMM yyyy HH:mm"
                      date={row.sold_at}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <Badge color={statusBadgeColor(row.status)}>
                      {row.status}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>{row.cashier_name ?? "-"}</Table.Cell>
                  <Table.Cell>{row.payment_method_name ?? "-"}</Table.Cell>
                  <Table.Cell>
                    <p className="text-right">
                      <Typography.Currency number={angka(row.sub_total)} />
                    </p>
                  </Table.Cell>
                  <Table.Cell>
                    <p className="text-right">
                      <Typography.Currency number={totalDiskon(row)} />
                    </p>
                  </Table.Cell>
                  {/* `amount_due`, bukan `grand_total`: yang terakhir tidak
                      memuat service charge, ongkir, dan diskon global. */}
                  <Table.Cell fontWeight="semibold">
                    <p className="text-right">
                      <Typography.Currency number={tagihanEfektif(row)} />
                    </p>
                  </Table.Cell>
                </Table.BodyRow>
              ))}
          </Table.Body>
        </Table.Wrapper>
        <Table.PaginationWrapper>
          <ServerSidePaginationButtons
            paginationProps={paginationProps}
            params={params}
            setParams={setParams}
          />
        </Table.PaginationWrapper>
      </Table.Container>
      {loader.state && (
        <p className="text-sm px-2 py-1 text-gray-500 font-light">
          Memuat data...
        </p>
      )}
    </MainLayout>
  );
}

export default StoreReportTransactionsPage;
